'use client';

import React from 'react';
import { Box } from '@mui/material';
import { color, spacing } from '@/theme/tokens';
import IconButtonGray from '@/components/formElements/iconButtons/IconButtonGray';
import IconButtonWhite from '@/components/formElements/iconButtons/IconButtonWhite';
import IconArrowBack from '@/assets/icons/IconArrowBack';
import IconArrowForward from '@/assets/icons/IconArrowForward';

interface NavigationControlsProps {
    currentSlide: number;
    totalSlides: number;
    onPrev: () => void;
    onNext: () => void;
    goToSlide: (index: number) => void;
    isDark?: boolean;
    nextLabel: string;
    previousLabel: string;
    // offset in % of the container height (different for reviews and locations)
    arrowsTopPositionOffset?: number;
}

export default function NavigationControls({
    currentSlide,
    totalSlides,
    onPrev,
    onNext,
    goToSlide,
    isDark = false,
    nextLabel,
    previousLabel,
    arrowsTopPositionOffset = 50, // 50% of the container height - default
}: NavigationControlsProps) {

    // Color scheme based on isDark prop
    const arrowColor = isDark ? color.gray[70] : color.white[0];
    const dotActiveColor = isDark ? color.gray[80] : color.white[0];
    const dotInactiveColor = isDark ? color.gray[30] : color.white[50];
    const hoverBgColor = isDark ? color.black[5] : color.white[10];

    const IconButtonVariant = isDark ? IconButtonGray : IconButtonWhite;

    return (
        <>
            {/* Desktop: arrows positioned outside */}
            <IconButtonVariant
                onClick={onPrev}
                aria-label={previousLabel}
                sx={{
                    display: { xs: 'none' },
                    '@media (min-width: 1563px)': {
                        display: 'inline-flex',
                    },
                    position: 'absolute',
                    left: `-${spacing[40]}`,
                    top: `${arrowsTopPositionOffset}%`,
                    transform: 'translateX(-100%) translateY(-200%)',
                    zIndex: 1,
                    color: arrowColor,
                    width: '46px',
                    height: '46px',
                    minWidth: '46px',
                    minHeight: '46px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': { backgroundColor: hoverBgColor },
                }}
            >
                <IconArrowBack width={22} height={22} color={arrowColor} />
            </IconButtonVariant>
            <IconButtonVariant
                onClick={onNext}
                aria-label={nextLabel}
                sx={{
                    display: { xs: 'none' },
                    '@media (min-width: 1563px)': {
                        display: 'inline-flex',
                    },
                    position: 'absolute',
                    right: `-${spacing[40]}`,
                    top: `${arrowsTopPositionOffset}%`,
                    transform: 'translateX(100%) translateY(-200%)',
                    zIndex: 1,
                    color: arrowColor,
                    width: '46px',
                    height: '46px',
                    minWidth: '46px',
                    minHeight: '46px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': { backgroundColor: hoverBgColor },
                }}
            >
                <IconArrowForward width={22} height={22} color={arrowColor} />
            </IconButtonVariant>
            <Box
                sx={{
                    display: { xs: 'none' },
                    '@media (min-width: 1563px)': {
                        display: 'flex',
                    },
                    justifyContent: 'center',
                }}
            >
                <Box sx={{ display: 'flex' }}>
                    {Array.from({ length: totalSlides }).map((_, index) => (
                        <Box
                            key={index}
                            onClick={() => goToSlide(index)}
                            sx={{
                                position: 'relative',
                                width: spacing[24],
                                height: spacing[24],
                                borderRadius: '50%',
                                cursor: 'pointer',
                                margin: 0,
                                padding: 0,
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: currentSlide === index ? spacing[8] : spacing[6],
                                    height: currentSlide === index ? spacing[8] : spacing[6],
                                    borderRadius: '50%',
                                    backgroundColor: currentSlide === index ? dotActiveColor : dotInactiveColor,
                                    transition: 'width 0.2s ease, height 0.2s ease, background-color 0.2s ease',
                                },
                            }}
                        />
                    ))}
                </Box>
            </Box>
            <Box
                sx={{
                    display: { xs: 'flex' },
                    '@media (min-width: 1563px)': {
                        display: 'none',
                    },
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: spacing[32],
                }}
            >
                <IconButtonVariant
                    onClick={onPrev}
                    aria-label={previousLabel}
                    sx={{
                        color: arrowColor,
                        width: '40px',
                        height: '40px',
                        '&:hover': { backgroundColor: hoverBgColor },
                    }}
                >
                    <IconArrowBack width={20} height={20} color={arrowColor} />
                </IconButtonVariant>
                <Box sx={{ display: 'flex' }}>
                    {Array.from({ length: totalSlides }).map((_, index) => (
                        <Box
                            key={index}
                            onClick={() => goToSlide(index)}
                            sx={{
                                position: 'relative',
                                width: spacing[24],
                                height: spacing[24],
                                borderRadius: '50%',
                                cursor: 'pointer',
                                margin: 0,
                                padding: 0,
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: currentSlide === index ? spacing[8] : spacing[6],
                                    height: currentSlide === index ? spacing[8] : spacing[6],
                                    borderRadius: '50%',
                                    backgroundColor: currentSlide === index ? dotActiveColor : dotInactiveColor,
                                    transition: 'width 0.2s ease, height 0.2s ease, background-color 0.2s ease',
                                },
                            }}
                        />
                    ))}
                </Box>
                <IconButtonVariant
                    onClick={onNext}
                    aria-label={nextLabel}
                    sx={{
                        color: arrowColor,
                        width: '46px',
                        height: '46px',
                        '&:hover': { backgroundColor: hoverBgColor },
                    }}
                >
                    <IconArrowForward width={20} height={20} color={arrowColor} />
                </IconButtonVariant>
            </Box>
        </>
    );
}
